import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { v4 as uuidv4 } from 'uuid';
import { MerchantService } from '../merchant.service';
import { AlertService } from '../../../../../_services/alert.service';
import { environment } from '../../../../../../environments/environment';

const { defaultCountryCode } = environment;

interface WalletRegistrationState {
  encKey: string;
  mobileNumber: string;
  firstName: string;
  lastName: string;
  nationalId: string;
  expirationDate: string;
  userType: string;
}

@Component({
  selector: 'app-wallet-registration',
  templateUrl: './wallet-registration.component.html',
  styleUrls: ['./wallet-registration.component.scss'],
})
export class WalletRegistrationComponent implements OnInit {
  data: WalletRegistrationState | null = null;
  phase: 'unavailable' | 'confirm' | 'otp' | 'done' = 'unavailable';
  sending = false;
  verifying = false;
  otpForm: FormGroup;
  otpError = '';
  isOtpSubmitted = false;
  private queryParams: any = {};
  private deviceId = '';
  private latitude = '';
  private longitude = '';

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private merchantService: MerchantService,
    private alertService: AlertService,
  ) {
    this.otpForm = this.formBuilder.group({
      otp: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => (this.queryParams = params));

    const state = (history.state || {}) as Partial<WalletRegistrationState>;
    if (state?.encKey && state?.mobileNumber) {
      this.data = state as WalletRegistrationState;
      this.phase = 'confirm';
    } else {
      this.phase = 'unavailable';
    }

    this.deviceId = this.getOrCreateDeviceId();
    this.captureGeolocation();
  }

  private getOrCreateDeviceId(): string {
    let uid = localStorage.getItem('uuid');
    if (!uid) {
      uid = uuidv4();
      localStorage.setItem('uuid', uid);
    }
    return uid;
  }

  private captureGeolocation(): void {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.latitude = String(position.coords.latitude);
        this.longitude = String(position.coords.longitude);
      },
      () => {
        // Permission denied or unavailable - headers will be sent empty.
      },
    );
  }

  get otp() {
    return this.otpForm.controls;
  }

  register() {
    if (!this.data) return;
    this.sending = true;
    this.merchantService
      .sendWalletOtp(
        {
          mobileNumber: this.data.mobileNumber,
          walletType: 'C',
          firstName: this.data.firstName,
          lastName: this.data.lastName,
          nationalId: this.data.nationalId,
          expirationDate: this.data.expirationDate,
          userType: this.data.userType,
        },
        {
          deviceId: this.deviceId,
          latitude: this.latitude,
          longitude: this.longitude,
        },
      )
      .subscribe({
        next: (res: any) => {
          this.sending = false;
          if (res?.response?.status == 200) {
            this.phase = 'otp';
            this.alertService.successAlert('An OTP has been sent to the client\'s phone number', '');
          } else {
            this.alertService.errorAlert({
              text: res?.response?.message || 'Unable to send OTP. Please try again.',
            });
          }
        },
        error: () => {
          this.sending = false;
          this.alertService.errorAlert({ text: 'Unable to send OTP. Please try again.' });
        },
      });
  }

  verify() {
    this.isOtpSubmitted = true;
    if (!this.data || this.otpForm.invalid) return;

    this.verifying = true;
    this.otpError = '';
    this.merchantService
      .verifyWalletOtp({
        encKey: this.data.encKey,
        otp: this.otpForm.value.otp,
        countryCode: defaultCountryCode,
        referenceNo: '0000000000',
        userType: this.data.userType,
        mobileNumber: this.data.mobileNumber,

      }, {
        deviceId: this.deviceId,
        latitude: this.latitude,
        longitude: this.longitude,
      })
      .subscribe({
        next: (res: any) => {
          this.verifying = false;
          if (res?.response?.status == 200) {
            this.phase = 'done';
          } else {
            this.otpError = res?.response?.message || 'Invalid OTP. Please try again.';
          }
        },
        error: (err: any) => {
          this.verifying = false;
          this.otpError = err?.error?.response?.message || 'Invalid OTP. Please try again.';
        },
      });
  }

  goToMerchantList() {
    this.router.navigate(['/merchants/merchant-master'], {
      queryParams: { ...this.queryParams },
    });
  }
}
